import React, { useRef, useState } from "react";
import { ArrowRight, Search } from "lucide-react";
import { trackEvent } from "@/lib/tracker";
import { gaTrack } from "@/lib/analytics";

/* A search bar that does not search. It starts a posting.

   Upwork and Fiverr both open with a search field, and the reason it works is
   not search: it is that you get proof before anyone asks you for anything. We
   want the opposite verb. A company that searches is browsing; a company that
   posts has told us what it needs and can be matched against every person who
   joins afterwards. So this borrows the affordance and changes the action.

   It hands off rather than answering here.

   An earlier version ran the match inline and rendered blurred cards under the
   field. That built a second, shorter version of a flow that already exists:
   the composer drafts the posting, shows what matches it, and carries the whole
   thing into signup. Answering in the hero meant a visitor saw their matches
   and then started again at the top of a flow that asked what they needed.
   Worse, a thin board turned the first interaction on the page into the words
   "nobody matches that yet", with nowhere to go from there.

   So the field collects the need and the posting path takes it from there,
   opened OVER this page rather than instead of it. See HeroFunnelModal: going
   from a hero full of faces to a bare form reads as the site emptying out, and
   keeping the page behind the panel keeps the evidence in view while the
   questions get asked.

   The suggestion chips are gone, removed 2026-09-14. They filled the field
   with a role rather than submitting, which was the right behaviour, but five
   example job titles under the only input on the page answer a question nobody
   asked and push the proof below the fold on a phone. The company side is not
   short of ideas about what to type; it is not starting at all. */

/* The placeholder asks the question rather than answering it.

   It has been through two other shapes. "Electrician for a 6 week fit-out in
   Carmel" taught the format but implied dates and a city were needed to get an
   answer, when the composer asks for both on the next screen. "CRNA or
   Anesthesiologist" named the work worth the most per engagement but read as a
   restriction: two job titles in a box look like the only two things the box
   takes. A plain question takes anything, which is true. */

export default function HeroNeedSearch({ onOpen }) {
  const [need, setNeed] = useState("");
  const inputRef = useRef(null);

  const go = () => {
    const raw = need.trim();
    if (raw.length < 3) { inputRef.current?.focus(); return; }

    /* Session storage rather than the URL: a job description is long, and it
       has no business sitting in a link somebody might share. */
    try { window.sessionStorage.setItem("sift_hero_need", raw); } catch { /* private mode */ }

    trackEvent("hero_need_submitted", {
      account_type: "organization", length: raw.length,
    });
    gaTrack("cta_get_started", { location: "hero_need_search", account_type: "organization" });
    // Meta gets Search from hero_need_submitted above: lib/analytics.js mirrorToMeta.

    /* Storage first, then open: CompanyDoors reads sift_hero_need in a state
       initialiser, so it has to be there before the panel mounts. */
    onOpen?.();
  };

  return (
    <div className="hns">
      <style>{CSS}</style>

      <div className="hns__row">
        <div className="hns__bar">
        <Search size={19} strokeWidth={2.2} aria-hidden="true" className="hns__icon" />
        <label htmlFor="hns-need" className="sr-only">What are you looking for?</label>
        <input
          id="hns-need" ref={inputRef} value={need}
          onChange={(e) => setNeed(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") go(); }}
          placeholder="What are you looking for?"
          enterKeyHint="go" autoComplete="off"
        />
        </div>

      {/* The action sits beside the field, not inside it. A button
          welded into the bar makes the whole control read as one lozenge, and
          on a phone it had to wrap inside its own container to fit. Out here it
          is plainly a button, it can be full width where that is the right
          answer, and the field is just a field. */}
        <button type="button" onClick={go} className="hns__go">
          Post for matches <ArrowRight size={15} strokeWidth={2.4} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

const CSS = `
/* The field runs the full width of the hero column, so it ends level with the
   last step of the flow below it rather than stopping two thirds of the way
   across. */
.hns { width: 100%; }

/* The affordance is the point: it has to read as a search bar at a glance,
   which means one enclosing shape, an icon on the left and a button on the
   right, not a form. */
/* One row: the field takes the slack and the button takes only what it needs,
   so the pair ends level with everything else on the page instead of the button
   pushing the field short. align-items:stretch keeps them the same height
   without either one having a height hard-coded to match the other. */
.hns__row { display:flex; align-items:stretch; gap:10px; width:100%; }

.hns__bar { flex:1 1 auto; min-width:0; display:flex; align-items:center; gap:11px;
  background:var(--surface); border:1px solid var(--border-strong);
  border-radius:var(--r-pill); padding:2px 18px; box-shadow:var(--shadow-md);
  transition:box-shadow .2s, border-color .2s; }
.hns__icon { color:var(--text-tertiary); flex:0 0 auto; }
.hns__bar input { flex:1 1 auto; min-width:0; border:0; outline:0; background:transparent;
  font-size:0.9375rem; color:var(--ink); padding:15px 0; font-family:var(--font-sans); }
.hns__bar input::placeholder { color:var(--text-tertiary); }
.hns__bar:focus-within { border-color:var(--brand); box-shadow:0 0 0 4px var(--brand-tint), var(--shadow-md); }

.hns__go { flex:0 0 auto; display:inline-flex; align-items:center; justify-content:center;
  gap:7px; background:var(--brand); color:#fff; border:0; cursor:pointer;
  border-radius:var(--r-pill); padding:0 26px; font-weight:700; font-size:0.90625rem;
  font-family:var(--font-sans); white-space:nowrap; box-shadow:var(--shadow-md);
  transition:background .2s; }
.hns__go:hover { background:var(--brand-hover); }
.hns__go:focus-visible { outline:2px solid var(--brand); outline-offset:3px; }

@media (max-width: 720px) {
  /* Side by side stops being balanced once the field is narrower than the
     button beside it, so below this they stack and the button goes full width.
     It is the only action on the screen; there is no reason for it to be a
     smaller target than the field above it. */
  .hns__row { flex-direction:column; gap:10px; }
  .hns__bar { padding:2px 16px; }
  .hns__bar input { padding:14px 0; font-size:1rem; }
  .hns__go { width:100%; padding:15px 24px; font-size:0.9375rem; }
}
`;
